import {
	DELIVERY_METHOD_KIND,
	VENDOR_AGGREGATION_GROUP,
} from "@dransay/api/models";
import { DismissableLayer } from "radix-ui/internal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router";
import { OPEN_DELIVERY_ADDRESS_MODAL_EVENT } from "~/features/delivery-address/delivery-address-modal.events";
import { useIsMobile } from "~/hooks/use-is-mobile";
import type { Route } from "../../routes/+types/_storefront";
import {
	type VendorCoords,
	VendorFilter,
	type VendorFilterProps,
} from "../products/filters/sections/vendor-filter.component";
import { useShippingPharmacyDraft } from "../products/pharmacy-picker/use-shipping-pharmacy-draft.hook";
import { MapPreviewCard } from "./map-preview-card.component";
import { mapVendorsToMarkers } from "./map-vendor-to-marker";
import { MobileFullscreenMapOverlay } from "./mobile-fullscreen-map-overlay.component";
import { PharmacyMapPanel } from "./pharmacy-map-panel.component";
import type { RadiusOptionValue } from "./radius-options";
import { RadiusSelect } from "./radius-select.component";
import { useCompanionMapSizing } from "./use-companion-map-sizing.hook";
import { useConfirmedPharmacySelection } from "./use-confirmed-pharmacy-selection.hook";
import { useDropdownCompanion } from "./use-dropdown-companion.hook";
import { useMapContentOverflow } from "./use-map-content-overflow.hook";
import { useMapExpressCourierFilter } from "./use-map-express-courier-filter.hook";
import { useMapRadiusVendors } from "./use-map-radius-vendors.hook";
import { useMobileFullMapOpen } from "./use-mobile-full-map-open.hook";
import { usePharmacySelection } from "./use-pharmacy-selection.hook";
import { usePostalCodeCenter } from "./use-postal-code-center.hook";
import { useShowAllMapMarkers } from "./use-show-all-map-markers.hook";
import { useVendorsFiltered } from "./use-vendors-filtered.hook";
import {
	additionalVendorsFromPreview,
	applyPharmacyOrGroupChange,
	commitPreviewOrStaleSelection,
	getDisplayedMapMarkers,
	optionalWhen,
	resolveHasStaleConfirmedSelection,
	resolveListRadiusKm,
	resolveVendorFilterFooterSlot,
	resolveVendorFilterOnChange,
	resolveVendorFilterValue,
	shouldKeepVendorPickerOpen,
} from "./vendor-filter-map-wrapper.utils";

const MAP_FALLBACK_WIDTH_PX = 420;

function isMapDeliveryMethod(
	deliveryMethodKind?: VendorFilterProps["deliveryMethodKind"],
) {
	return deliveryMethodKind === "pickup" || deliveryMethodKind === "courier";
}

/**
 * Wraps VendorFilter and renders a companion map portal
 * inside the Radix Select dropdown content when open.
 *
 * By portaling into the content element, the map becomes a DOM
 * child of DismissableLayer's node. Radix's `node.contains(target)`
 * check returns true for map interactions, preventing the dropdown
 * from closing on map click, drag, or touch.
 */
export function VendorFilterMapWrapper(props: VendorFilterProps) {
	const { t } = useTranslation("product");
	const isMobile = useIsMobile();
	const storefrontLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>(
			"routes/_storefront",
		);
	const enableTopPharmacyCartPromotion =
		storefrontLoaderData?.enableTopPharmacyCartPromotion ?? false;
	const enablePharmacyFilters =
		storefrontLoaderData?.enablePharmacyFilters ?? false;
	const { showAllMapMarkers, handleMapShowAllMarkersChange } =
		useShowAllMapMarkers(props.value, enableTopPharmacyCartPromotion);
	const enablePharmacyMap = isMapDeliveryMethod(props.deliveryMethodKind);
	// Figma 602 "Radius selection" + finding 4598:88707: pickup only. Courier
	// availability is the pharmacy's delivery zone, not the customer's radius,
	// so the chip would narrow the list on a distance that decides nothing.
	const showRadiusControl =
		enablePharmacyFilters && props.deliveryMethodKind === "pickup";
	const {
		center,
		isLoading: isCenterLoading,
		postalCode,
	} = usePostalCodeCenter({ enabled: enablePharmacyMap });
	const { confirmedSelection, setConfirmedSelection } =
		useConfirmedPharmacySelection(props.value, postalCode);
	const listingCoordsRef = useRef<Map<number, VendorCoords>>(new Map());
	const [expressSelected, setExpressSelected] = useState(false);
	const expressCourierOnly =
		enablePharmacyFilters &&
		props.deliveryMethodKind === DELIVERY_METHOD_KIND.COURIER &&
		postalCode !== "" &&
		expressSelected;
	/*
	 * Markers and radius rows share one unbounded getVendorsFiltered page.
	 * Radius options are bucketed client-side from that response.
	 */
	const {
		items: filteredVendors,
		metadata: filteredVendorsMetadata,
		aggregatedCount: filteredAggregatedCount,
		isLoading: isVendorsLoading,
		hasError: hasVendorsError,
		expressCheckFailed,
	} = useVendorsFiltered({
		enabled: enablePharmacyMap,
		deliveryMethodKind: props.deliveryMethodKind,
		expressCourierOnly,
	});
	const {
		showFilter: showExpressFilter,
		vendors: expressVendors,
		checkFailed: hasExpressCheckError,
	} = useMapExpressCourierFilter({
		enablePharmacyFilters,
		deliveryMethodKind: props.deliveryMethodKind,
		postalCode,
		vendors: filteredVendors,
		selected: expressSelected,
		setSelected: setExpressSelected,
		expressCheckFailed,
	});
	const allVendors = useMemo(
		() => mapVendorsToMarkers(expressVendors),
		[expressVendors],
	);
	/**
	 * The radius rows count what the active pharmacy-type selection can actually
	 * show (FR-016/FR-017: a row is disabled when that selection has nothing
	 * inside it), so they need the same Top/non-Top narrowing
	 * `getDisplayedMapMarkers` applies. Counting the unnarrowed set would
	 * advertise numbers the map contradicts — "10 km · 42" beside three visible
	 * markers — and leave rows enabled that resolve to an empty map.
	 */
	const radiusScopedVendors = useMemo(
		() =>
			enableTopPharmacyCartPromotion && !showAllMapMarkers
				? allVendors.filter((vendor) => vendor.featured)
				: allVendors,
		[allVendors, enableTopPharmacyCartPromotion, showAllMapMarkers],
	);
	const {
		radius,
		radiusOptions,
		selectedRadius,
		setSelectedRadius,
		vendors,
		externalVendorsFiltered,
	} = useMapRadiusVendors({
		showRadiusControl,
		enablePharmacyMap,
		allVendors,
		radiusScopedVendors,
		filteredVendors: expressVendors,
		filteredAggregatedCount,
		isVendorsLoading,
	});
	const isMapLoading = isCenterLoading || isVendorsLoading;
	const hasFetchedVendors = filteredVendorsMetadata !== null;
	/**
	 * An error response (getVendorsFiltered 400s on an ungeocodable postal code)
	 * carries no metadata, so `hasFetchedVendors` alone reads as "not fetched
	 * yet" and would suppress the empty state — leaving a blank map with no
	 * markers and no explanation. Treat it as "no results" so MapEmptyState
	 * prompts for a different postal code.
	 */
	const hasVendorResults =
		!hasVendorsError &&
		!hasExpressCheckError &&
		(!hasFetchedVendors || vendors.length > 0);

	const { wrapperRef, isOpen, contentElement } = useDropdownCompanion();
	// The companion hook watches the Select trigger's data-state, which the
	// mobile bottom sheet (Figma 4643:88826) never flips — the sheet reports
	// through onDropdownOpenChange instead, so picker-open logic listens to both.
	const [reportedPickerOpen, setReportedPickerOpen] = useState(false);
	const propsOnDropdownOpenChange = props.onDropdownOpenChange;
	const handleDropdownOpenChange = useCallback(
		(open: boolean) => {
			setReportedPickerOpen(open);
			propsOnDropdownOpenChange?.(open);
		},
		[propsOnDropdownOpenChange],
	);
	const isPickerOpen = isOpen || reportedPickerOpen;

	const hasStaleConfirmedSelection = resolveHasStaleConfirmedSelection({
		value: props.value,
		confirmedSelection,
		postalCode,
	});
	const effectiveConfirmedValue = hasStaleConfirmedSelection
		? VENDOR_AGGREGATION_GROUP.ALL
		: props.value;

	const handleConfirmSelection = useCallback(
		(vendorId: string) => {
			setConfirmedSelection({ vendorId, postalCode });
			props.onChange(vendorId);
		},
		[postalCode, props.onChange, setConfirmedSelection],
	);

	const {
		previewId,
		announcementMessage,
		onPreviewSelect,
		onConfirmSelection,
		onDismiss,
		onClearPreview,
	} = usePharmacySelection({
		confirmedValue: effectiveConfirmedValue,
		dismissValue: props.value,
		onConfirm: handleConfirmSelection,
		isOpen: isPickerOpen,
	});

	// Figma 4597:88702: a bulk row ("alle Top-Apotheken" / all) is a draft like
	// a pharmacy card — highlighted, committed by the CTA — instead of applying
	// and closing the picker on tap.
	const [draftGroupId, setDraftGroupId] = useState<string | null>(null);
	useEffect(() => {
		if (isPickerOpen) {
			return;
		}
		setDraftGroupId(null);
		handleMapShowAllMarkersChange(props.value === VENDOR_AGGREGATION_GROUP.ALL);
	}, [isPickerOpen, props.value, handleMapShowAllMarkersChange]);

	const handleVendorDataChange = useCallback((coords: VendorCoords[]) => {
		for (const c of coords) {
			listingCoordsRef.current.set(c.id, c);
		}
	}, []);

	const showMap = enablePharmacyMap && isOpen && contentElement !== null;
	const { mapWidth } = useCompanionMapSizing({
		wrapperRef,
		contentElement,
		showMap,
		fallbackWidth: MAP_FALLBACK_WIDTH_PX,
	});

	useMapContentOverflow(contentElement, showMap);

	const displayedMarkers = useMemo(
		() =>
			getDisplayedMapMarkers({
				vendors,
				allVendors,
				deliveryMethodKind: props.deliveryMethodKind,
				enableTopPharmacyCartPromotion,
				showAllMapMarkers,
				previewId,
				listingCoords: listingCoordsRef.current,
			}),
		[
			vendors,
			allVendors,
			previewId,
			props.deliveryMethodKind,
			enableTopPharmacyCartPromotion,
			showAllMapMarkers,
		],
	);

	const closeDropdown = useCallback(() => {
		document.dispatchEvent(
			new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
		);
	}, []);
	const {
		isMobileFullMapOpen,
		setIsMobileFullMapOpen,
		handleOpenFullMap,
		closeFullMap,
	} = useMobileFullMapOpen(isPickerOpen, enablePharmacyMap, closeDropdown);
	const useShippingDraft = enablePharmacyFilters && !enablePharmacyMap;
	const handleShippingCommit = useCallback(
		(vendorId: string) => {
			props.onChange(vendorId);
			closeDropdown();
		},
		[props.onChange, closeDropdown],
	);
	const {
		draft: shippingDraft,
		isDirty: shippingDraftDirty,
		onDraftSelect: onShippingDraftSelect,
		onConfirm: onShippingConfirm,
	} = useShippingPharmacyDraft({
		confirmedValue: props.value,
		onCommit: handleShippingCommit,
		isOpen: useShippingDraft && isPickerOpen,
	});

	// "Enter the address" CTA in the no-address empty state opens the
	// delivery-address modal (the storefront's address entry point).
	const handleEnterAddress = useCallback(() => {
		window.dispatchEvent(new CustomEvent(OPEN_DELIVERY_ADDRESS_MODAL_EVENT));
	}, []);

	const handleConfirm = useCallback(() => {
		commitPreviewOrStaleSelection({
			previewId,
			onConfirmSelection,
			draftGroupId,
			hasStaleConfirmedSelection,
			onChange: props.onChange,
		});
		closeDropdown();
	}, [
		previewId,
		onConfirmSelection,
		draftGroupId,
		hasStaleConfirmedSelection,
		props.onChange,
		closeDropdown,
	]);

	const handleFullscreenConfirm = useCallback(() => {
		commitPreviewOrStaleSelection({
			previewId,
			onConfirmSelection,
			draftGroupId,
			hasStaleConfirmedSelection,
			onChange: props.onChange,
		});
		closeFullMap();
	}, [
		previewId,
		onConfirmSelection,
		draftGroupId,
		hasStaleConfirmedSelection,
		props.onChange,
		closeFullMap,
	]);

	const handleChange = useCallback(
		(vendorId?: string) => {
			applyPharmacyOrGroupChange({
				vendorId,
				onPharmacy: (pharmacyId) => {
					setDraftGroupId(null);
					onPreviewSelect(pharmacyId);
				},
				onGroup: (groupId) => {
					onClearPreview();
					setDraftGroupId(groupId);
				},
			});
		},
		[onPreviewSelect, onClearPreview],
	);

	const handleMarkerClick = useCallback(
		(pharmacyId: number) => {
			onPreviewSelect(String(pharmacyId));
		},
		[onPreviewSelect],
	);

	const shouldUsePharmacyVariant = enablePharmacyMap;

	const handleConfirmRef = useRef(handleConfirm);
	handleConfirmRef.current = handleConfirm;

	const stableHandleConfirm = useCallback(() => {
		handleConfirmRef.current();
	}, []);

	const renderConfirmButton = useCallback(
		(
			onClick: () => void,
			testId: string,
			disabled = false,
		): React.ReactNode => (
			<button
				type="button"
				className="h-14 w-full rounded-xl bg-ansay-light p-4 text-body-emphasis text-gray-700 transition-opacity enabled:hover:opacity-90 disabled:bg-gray-200 disabled:text-gray-500"
				onClick={onClick}
				data-testid={testId}
				disabled={disabled}
			>
				{t("vendor.map.selectPharmacy")}
			</button>
		),
		[t],
	);

	const mobileMapSlot = useMemo(() => {
		if (!enablePharmacyMap || !isMobile) {
			return undefined;
		}
		return (
			<MapPreviewCard
				center={center}
				pharmacyMarkers={displayedMarkers}
				selectedPharmacyId={previewId}
				onOpenFullMap={handleOpenFullMap}
				isLoading={isMapLoading}
				onEnterAddress={handleEnterAddress}
			/>
		);
	}, [
		center,
		displayedMarkers,
		enablePharmacyMap,
		isMobile,
		previewId,
		isMapLoading,
		handleOpenFullMap,
		handleEnterAddress,
	]);

	/**
	 * When a map marker is selected, immediately inject that vendor into the
	 * dropdown options using data already available from the vendors query.
	 * This avoids any async fetch delay and ensures the item is highlighted
	 * as soon as the marker is clicked. For vendors beyond the fetched set,
	 * VendorFilter's own selectedVendorFetcher handles the fetch.
	 */
	const additionalVendors = useMemo(
		() => additionalVendorsFromPreview(previewId, allVendors),
		[previewId, allVendors],
	);

	return (
		<div ref={wrapperRef} className="w-full min-w-0">
			{/* Screen reader announcements for selection changes */}
			<div aria-live="polite" aria-atomic="true" className="sr-only">
				{announcementMessage}
			</div>
			<div className="flex items-stretch gap-2">
				<div className="min-w-0 flex-1">
					<VendorFilter
						{...props}
						hidePostalCodeInput={true}
						value={resolveVendorFilterValue({
							useShippingDraft,
							shippingDraft,
							propsValue: props.value,
							shouldUsePharmacyVariant,
							previewId,
							draftGroupId,
							isPickerOpen,
							effectiveConfirmedValue,
						})}
						onChange={resolveVendorFilterOnChange({
							useShippingDraft,
							onShippingDraftSelect,
							shouldUsePharmacyVariant,
							handleChange,
							propsOnChange: props.onChange,
						})}
						onDropdownOpenChange={handleDropdownOpenChange}
						mobileMapSlot={mobileMapSlot}
						variant="pharmacy"
						keepOpenOnSelect={shouldKeepVendorPickerOpen(
							shouldUsePharmacyVariant,
							useShippingDraft,
						)}
						footerSlot={resolveVendorFilterFooterSlot({
							useShippingDraft,
							shouldUsePharmacyVariant,
							shippingConfirm: renderConfirmButton(
								onShippingConfirm,
								"pharmacy-shipping-confirm-cta",
								!shippingDraftDirty,
							),
							mapConfirm: renderConfirmButton(
								stableHandleConfirm,
								"pharmacy-map-confirm-cta",
							),
						})}
						onVendorDataChange={optionalWhen(
							shouldUsePharmacyVariant,
							handleVendorDataChange,
						)}
						additionalVendors={optionalWhen(
							shouldUsePharmacyVariant,
							additionalVendors,
						)}
						onIncludeNonTopPharmaciesChange={optionalWhen(
							enableTopPharmacyCartPromotion,
							handleMapShowAllMarkersChange,
						)}
						radiusControl={optionalWhen(
							showRadiusControl,
							<RadiusSelect
								value={radius}
								pharmacyMarkers={radiusScopedVendors}
								onChange={setSelectedRadius}
								disabled={center === null}
							/>,
						)}
						/*
						 * Figma 602: the mobile radius trigger lives inside the same filter
						 * row as Kurier/Top-Apotheke/Pay (PharmacyPickerFilterCards'
						 * mobileRadiusControl slot), not as a sibling of the picker like it
						 * used to be. It's still wrapped in `DismissableLayer.Branch`: its
						 * own Sheet.Content portals to document.body, outside the picker's
						 * Radix Select content, so without this the Select would still treat
						 * an interaction inside the open sheet as an outside pointer-down and
						 * dismiss itself (taking the map with it). `className="contents"`
						 * keeps the wrapper out of the filter row's flex layout.
						 */
						mobileRadiusControl={optionalWhen(
							showRadiusControl,
							<DismissableLayer.Branch className="contents">
								<RadiusSelect
									variant="sheet"
									value={radius}
									pharmacyMarkers={radiusScopedVendors}
									onChange={setSelectedRadius}
									disabled={center === null}
								/>
							</DismissableLayer.Branch>,
						)}
						expressSelected={optionalWhen(showExpressFilter, expressSelected)}
						onExpressChange={optionalWhen(
							showExpressFilter,
							setExpressSelected,
						)}
						expressCheckFailedMessage={optionalWhen(
							hasExpressCheckError,
							t("vendor.picker.express.checkFailed"),
						)}
						externalVendorsFiltered={externalVendorsFiltered}
						listRadiusKm={resolveListRadiusKm({
							hasExternalVendorsFiltered: externalVendorsFiltered != null,
							showRadiusControl,
							radius,
						})}
					/>
				</div>
			</div>
			{showMap &&
				createPortal(
					<div
						className="hidden md:block rounded-r-2xl overflow-hidden shadow-lg"
						style={{
							position: "absolute",
							top: 0,
							left: "100%",
							width: `${mapWidth}px`,
							height: "100%",
							zIndex: 50,
						}}
						data-testid="pharmacy-map-companion"
					>
						<PharmacyMapPanel
							center={center}
							pharmacyMarkers={displayedMarkers}
							selectedPharmacyId={previewId}
							onPharmacyMarkerClick={handleMarkerClick}
							onDismissPreview={onDismiss}
							isLoading={isMapLoading}
							hasVendorResults={hasVendorResults}
							onEnterAddress={handleEnterAddress}
						/>
					</div>,
					contentElement,
				)}
			<MobileFullscreenMapOverlay
				isOpen={enablePharmacyMap && isMobileFullMapOpen}
				preload={
					enablePharmacyMap && isMobile && isPickerOpen && !isMobileFullMapOpen
				}
				center={center}
				pharmacyMarkers={displayedMarkers}
				selectedPharmacyId={previewId}
				onPharmacyMarkerClick={handleMarkerClick}
				onDismissPreview={onDismiss}
				onConfirmSelection={handleFullscreenConfirm}
				onClose={() => {
					onDismiss();
					closeFullMap();
				}}
				hasVendorResults={hasVendorResults}
				isLoading={isMapLoading}
				radiusControl={optionalWhen(
					showRadiusControl,
					<RadiusSelect
						variant="sheet"
						value={radius}
						pharmacyMarkers={radiusScopedVendors}
						onChange={setSelectedRadius}
						disabled={center === null}
					/>,
				)}
			/>
		</div>
	);
}
